import React, {useMemo} from "react";
import {Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import ChartCard from "./ChartCard.jsx";
import useStatisticsData from "./useStatisticsData.js";
import {formatEuro, formatEuroCompact, formatPercent, GROUP_PALETTE} from "./format.js";

const MONTH_LABELS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

/** #9 Saisonalität - Verteilung des Umsatzes über die zwölf Kalendermonate.
 * Eine einzelne Datenreihe -> einheitliche Primärfarbe, keine Legende. */
export default function SeasonalityChart({filters}) {
    const {data, loading, error} = useStatisticsData("/statistics/seasonality", filters);

    const rows = useMemo(() => {
        const byMonth = new Map((data?.rows || []).map((row) => [row.month, row]));
        return Array.from({length: 12}, (_, index) => {
            const month = index + 1;
            const row = byMonth.get(month);
            return {
                month,
                label: MONTH_LABELS[index],
                net_amount: row?.net_amount || 0,
                net_share_pct: row?.net_share_pct ?? null,
            };
        });
    }, [data]);
    const hasData = rows.some((row) => row.net_amount > 0);

    return (
        <ChartCard
            title="Saisonalität"
            help="Anteil jedes Kalendermonats am Nettoumsatz über alle Jahre des gewählten Zeitraums - zeigt, ob ein Sortiment saisonal verkauft wird."
            loading={loading}
            error={error}
            empty={!loading && !hasData}
            height={280}
        >
            <ResponsiveContainer width="100%" height={280}>
                <BarChart data={rows} margin={{top: 8, right: 12, left: 0, bottom: 0}}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--bs-border-color, #dee2e6)" />
                    <XAxis dataKey="label" tick={{fontSize: 12}} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={formatEuroCompact} tick={{fontSize: 12}} axisLine={false} tickLine={false} width={70} />
                    <Tooltip
                        formatter={(value) => [formatEuro(value), "Nettoumsatz"]}
                        labelFormatter={(label, payload) => {
                            const row = payload?.[0]?.payload;
                            return row?.net_share_pct != null ? `${label} (${formatPercent(row.net_share_pct)})` : label;
                        }}
                    />
                    <Bar dataKey="net_amount" fill={GROUP_PALETTE[0]} radius={[4, 4, 0, 0]} barSize={28} />
                </BarChart>
            </ResponsiveContainer>
        </ChartCard>
    );
}
